import { Button, Group, Text, Menu, useMantineTheme, createStyles, ScrollArea, HoverCard, Stack, Input } from "@mantine/core"
import { getHotkeyHandler, useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CaretRight, ChevronDown, ChevronRight, Minus, Plus, Search } from "tabler-icons-react"
import { PortalLink } from "../apis/response/GetPortalResponse";
import { CurrentLocale } from "../App";
import { GetTranslated, TranslateField } from "../types/translate-field"

const useStyles = createStyles((theme) => ({
    catContainer: {
        width: '100%',
        margin: 'auto',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        overflow: 'hidden',
        justifyContent: 'center',
        // [theme.fn.smallerThan('md')]: {
        //     display: 'none'
        // },
        backgroundColor: `${theme.colors.hkc[0]}EE`,
        // padding: '0px 10px'
    },
    blockBtn: {
        backgroundColor: 'unset',
        width: '35%',
        padding: 7,
        height: 'auto',
        borderRadius: 0,
        [theme.fn.smallerThan('md')]: {
            width: '50%'
        }
    },
    bordered: {
        border: `1px solid ${theme.colors.hkc[0]}`
    },
    btnText: {
        color: 'white',
        '&:hover': {
            color: theme.colors.hkc[0],
        }
    },
    menuDropdown: {
        backgroundColor: `${theme.colors.hkc[0]}EE`,
        border: `1px solid ${theme.colors.hkc[0]}`,
        borderRadius: 0,
        padding: 0
    },
    usefulLink: {
        fontSize: 14,
        padding: 7,
        height: 'auto',
        borderRadius: 0,
        color: 'white',
        textDecoration: 'unset',
        '&:hover': {
            color: theme.colors.hkc[0],
            backgroundColor: 'white'
        }
    },
    mobileDisabled: {
        [theme.fn.smallerThan('md')]: {
            display: 'none'
        }
    },
    mobileSearchBar: {
        margin: '10px 0px',
        [theme.fn.largerThan('md')]: {
            display: 'none'
        }
    }
}));

export interface CategoryMenu {
    label: TranslateField; slug: string; display_order:number;
}

export interface CategoryMap {
    categories: CategoryMenu[]
    departments: {
        info: CategoryMenu
        subCat: CategoryMenu[]
    }[]
}

interface CategoryNavbarProps {
    short?: boolean
    data: CategoryMap
    useful: PortalLink[]
    category?: string
    department?: string
}

const CategoryNavbar = (props: CategoryNavbarProps) => {
    const { t } = useTranslation('common', { keyPrefix: 'section' })
    const header = useTranslation('header');
    const theme = useMantineTheme()
    const { classes } = useStyles()

    const [category, setCategory] = useState('')
    const [selectedCategory, selectCategory] = useState(
        props.category ?
            GetTranslated(props.data.categories.filter(x => x.slug.includes(props.category!))[0].label, CurrentLocale)
            : '')
    const [catId, setCatId] = useState(props.category ?? '')
    const [catOnHover, setCatOnHover] = useState(false)

    const [department, setDepartment] = useState(`/Department/${props.department ?? 'D1'}`)
    const [deptOnHover, setDeptOnHover] = useState(false)

    const [subCat, hoverSubCat] = useState('')
    const [useful, hoverUseful] = useState(false)

    const mapping = props.data

    const onCategorySelect = (cat: string, id: string) => {
        setCatId(id)
        selectCategory(cat)
        setCategory('')
        setCatOnHover(false)
    }

    const getCatId = (cat: string) => {
        const arr = cat.split('/')
        return arr[arr.length - 1]
    }

    const getDeptSlug = (slug: string) => {
        const cat = slug.split('/')[0]
        return {
            cat: cat,
            slug: slug.replace(cat, '')
        }
    }

    const getFirstDepartment = () => {
        const first = mapping.departments.filter(x =>
            props.department ?
                (x.info.slug.includes(props.department!))
                :
                (catId == '' || getDeptSlug(x.info.slug).cat == catId)
        )[0]
        return getDeptSlug(first.info.slug).slug
    }

    const mobile = useMediaQuery('min-width: 1000px')
    const [search, inputSearch] = useState('')

    const nav = useNavigate()
    const isShort = props.short ?? false

    const navigate = (url: string) => {
        window.location.replace(url)
    }

    return (
        <>
            <Group spacing={0} className={`${classes.mobileDisabled} ${classes.catContainer}`} id='cat-container'>
                <Menu position='bottom-end' offset={10} shadow={'md'} width={300} trigger='hover'>
                    <Menu.Target>
                        <Button onMouseOver={() => setCatOnHover(true)} onMouseOut={() => setCatOnHover(false)}
                            className={`${classes.bordered} ${classes.blockBtn}`} radius={0}
                            rightIcon={<ChevronDown size={24} style={{ color: catOnHover ? theme.colors.hkc[0] : 'white' }} />}
                            style={{ backgroundColor: !catOnHover ? theme.colors.hkc[0] : 'white' }}
                        >
                            <Text style={{ color: catOnHover ? theme.colors.hkc[0] : 'white', fontWeight: 500, fontSize: 18 }}>
                                {selectedCategory != '' ? selectedCategory : t('category')}
                            </Text>
                        </Button>
                    </Menu.Target>
                    <Menu.Dropdown className={classes.menuDropdown}>
                        <ScrollArea type='always' style={{ height: isShort ? 270 : 'unset' }} offsetScrollbars>
                            {mapping.categories.map(cat => {
                                return (
                                    <Menu.Item key={cat.slug} component={'div'} style={{ padding: 0 }} onMouseOver={() => setCategory(cat.slug)} onMouseOut={() => setCategory('')}>
                                        <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between' }}>
                                            <Button onClick={() => navigate(cat.slug)} style={{ flexGrow: 1, backgroundColor: category != cat.slug ? `unset` : 'white' }} className={classes.blockBtn} >
                                                <Text style={{ color: category == cat.slug ? theme.colors.hkc[0] : 'white', fontWeight: CurrentLocale == 'en' ? 500 : 'normal' }}>
                                                    {GetTranslated(cat.label, CurrentLocale)}
                                                </Text>
                                            </Button>
                                            {
                                                selectedCategory != GetTranslated(cat.label, CurrentLocale) ?
                                                    <Button variant="subtle" style={{ height: 'unset', backgroundColor: 'unset' }} onClick={() => { onCategorySelect(GetTranslated(cat.label, CurrentLocale), getCatId(cat.slug)) }}>
                                                        <Plus style={{ color: category == cat.slug ? theme.colors.hkc[0] : 'white' }} />
                                                    </Button>
                                                    :
                                                    <Button variant="subtle" style={{ height: 'unset', backgroundColor: 'unset' }} onClick={() => { onCategorySelect('', '') }}>
                                                        <Minus style={{ color: category == cat.slug ? theme.colors.hkc[0] : 'white' }} />
                                                    </Button>
                                            }
                                        </div>
                                    </Menu.Item>
                                )
                            })}
                        </ScrollArea>
                    </Menu.Dropdown>
                </Menu>
                <HoverCard position={'bottom'} offset={10} shadow={'md'} onOpen={() => setDepartment(getFirstDepartment())} onClose={() => setDepartment(getFirstDepartment())}>
                    <HoverCard.Target>
                        {/* <Button className={classes.blockBtn} radius={0} rightIcon={<ChevronDown />}>
                            <Text style={{color: 'white'}}>
                            {'category'}
                            </Text>
                        </Button> */}
                        <Button onMouseOver={() => setDeptOnHover(true)} onMouseOut={() => setDeptOnHover(false)}
                            className={`${classes.bordered} ${classes.blockBtn}`} radius={0}
                            rightIcon={<ChevronDown size={28} style={{ color: deptOnHover ? theme.colors.hkc[0] : 'white' }} />}
                            style={{ backgroundColor: !deptOnHover ? theme.colors.hkc[0] : 'white' }}
                        >
                            <Text style={{ color: deptOnHover ? theme.colors.hkc[0] : 'white', fontWeight: 500, fontSize: 18 }}>
                                {
                                    props.department ?
                                        GetTranslated(props.data.departments.filter(x => x.info.slug.includes(props.department!))[0].info.label, CurrentLocale)
                                        : t('department')
                                }
                            </Text>
                        </Button>
                    </HoverCard.Target>
                    <HoverCard.Dropdown className={classes.menuDropdown}>
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                            <ScrollArea style={{ height: isShort ? 270 : 'unset', width: 200 }} offsetScrollbars>
                                {mapping.departments.filter(x => catId == '' || getDeptSlug(x.info.slug).cat == catId).map(dept => {
                                    const slug = getDeptSlug(dept.info.slug).slug
                                    return (
                                        <Button onClick={() => navigate(slug)} radius={0} fullWidth style={{ padding: 7, height: 'unset', backgroundColor: department != slug ? 'unset' : 'white' }}
                                            onMouseOver={() => setDepartment(slug)}
                                            rightIcon={department == slug ? <ChevronRight style={{ color: theme.colors.hkc[0] }} /> : <ChevronDown style={{ color: 'white' }} />}
                                        >
                                            <Text style={{ color: department == slug ? theme.colors.hkc[0] : 'white', fontWeight: CurrentLocale == 'en' ? 500 : 'normal'  }}>
                                                {GetTranslated(dept.info.label, CurrentLocale)}
                                            </Text>
                                        </Button>
                                    )
                                })}
                            </ScrollArea>
                            <div style={{ width: 350, backgroundColor: 'white' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '15px 30px', alignItems: 'start' }}>
                                    <Text style={{ color: theme.colors.hkc[1], fontWeight: 500, marginBottom: 10 }}>{t('sub-category')}</Text>
                                    <div style={{ borderBottom: `5px solid ${theme.colors.hkc[0]}`, width: 200 }}></div>
                                </div>
                                <ScrollArea type='always' style={{ width: '100%', maxHeight: isShort ? 200 : 'unset', display: 'flex', flexDirection: 'column', alignItems: 'start' }} offsetScrollbars>
                                    <Stack spacing={0}>
                                        {mapping.departments.find(x => getDeptSlug(x.info.slug).slug == department)!.subCat.filter((v, i, a) => a.map(x => x.slug)[i] == v.slug).map(sc => {
                                            return (
                                                <Button onClick={() => navigate(sc.slug)} fullWidth variant='white' onMouseOver={() => hoverSubCat(sc.slug)} onMouseOut={() => hoverSubCat('')} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}>
                                                    {subCat == sc.slug ? <CaretRight style={{ color: theme.colors.hkc[1] }} /> : <></>}
                                                    <Text style={{ fontWeight: 500, color: theme.colors.hkc[0], textAlign: 'left' }}>{GetTranslated(sc.label, CurrentLocale)}</Text>
                                                </Button>
                                            )
                                        })}
                                    </Stack>
                                </ScrollArea>
                            </div>
                        </div>
                    </HoverCard.Dropdown>
                </HoverCard>
                <HoverCard >
                    <HoverCard.Target>
                        <Button onMouseOver={() => hoverUseful(true)} onMouseOut={() => hoverUseful(false)}
                            className={`${classes.bordered} ${classes.blockBtn}`} radius={0}
                            rightIcon={<ChevronDown size={28} style={{ color: useful ? theme.colors.hkc[0] : 'white' }} />}
                            style={{ backgroundColor: !useful ? theme.colors.hkc[0] : 'white' }}
                        >
                            <Text style={{ color: useful ? theme.colors.hkc[0] : 'white', fontWeight: 500, fontSize: 18 }}>
                                {t('useful-links')}
                            </Text>
                        </Button>
                    </HoverCard.Target>
                    <HoverCard.Dropdown className={classes.menuDropdown}>
                        <ScrollArea style={{ height: isShort ? 'min(100%, 270px)' : 'unset' }}>
                            <Stack style={{ width: 300 }} spacing={0}>
                                {props.useful.map(x => {
                                    return (
                                        <a href={x.link} target={'_blank'} className={classes.usefulLink}>
                                            {x.name}
                                        </a>
                                    )
                                })}
                            </Stack>
                        </ScrollArea>
                    </HoverCard.Dropdown>
                </HoverCard>
            </Group>
            <div className={classes.mobileSearchBar}>
                <Input placeholder={header.t('search')} icon={<Search size={14} />}
                    onChange={(x: any) => inputSearch(x.target.value)}
                    onKeyDown={
                        getHotkeyHandler([
                            ['Enter', () => nav(`/search/${search}`, { replace: true })]
                        ])
                    }
                    rightSection={search != '' ?
                        <Button component={Link} to={`/search/${search}`} style={{ backgroundColor: theme.colors.hkc[0] }}>{header.t('search')}</Button>
                        : <></>} />
            </div>
        </>
    )
}

export default CategoryNavbar